import { Button, Modal } from "@xola/ui-kit";
import { now, requiredNumberMoreThanZero } from "common";
import { isNil } from "lodash";
import React from "react";
import { BaseSchedule } from "types";
import { array, number, object, ref } from "yup";
import { ScrollToFieldError } from "../../helpers/validationHelper";
import { useSeller } from "../../hooks/useAccount";
import { createBaseSchedule } from "../../models/BaseSchedule";
import { SecondaryButton } from "../Common/SecondaryButton";
import { XolaFormik } from "../Common/XolaFormik";
import { BaseScheduleEditor, BaseScheduleEditorProps } from "./BaseScheduleEditor";

interface BaseScheduleEditorModalProps extends ModalContentProps {
    isOpen: boolean;
}

export const BaseScheduleEditorModal = (props: BaseScheduleEditorModalProps) => {
    const handleClose = () => {
        props.onClose();
    };

    return (
        <Modal isOpen={props.isOpen} size="huge" onClose={handleClose}>
            <ModalContent {...props} />
        </Modal>
    );
};

const validationSchema = object({
    timeRanges: array(
        object({
            startTime: requiredNumberMoreThanZero(),
            endTime: requiredNumberMoreThanZero().when("startTime", {
                is: (startTime?: number) => !isNil(startTime),
                then: number().moreThan(ref("startTime"), "Must be after start time"),
            }),
        }),
    ),
});

interface ModalContentProps extends Pick<BaseScheduleEditorProps, "disablePastDays" | "weekdaysType"> {
    title: string;
    description?: string;
    subtitle?: string;
    initialValues?: BaseSchedule;
    onClose: () => void;
    onApply: (values: BaseSchedule) => void;
}

const ModalContent = ({
    initialValues,
    title,
    description,
    subtitle,
    onClose,
    onApply,
    disablePastDays,
    weekdaysType,
}: ModalContentProps) => {
    const seller = useSeller();

    const initialFormValues =
        initialValues ??
        createBaseSchedule({
            start: now().tz(seller.timezoneName, true).startOf("day"),
        });

    const handleCancelClick = () => {
        onClose();
    };

    const handleSubmit = (values: BaseSchedule) => {
        onApply(values);
        onClose();
    };

    return (
        <XolaFormik
            validateOnChange={false}
            validationSchema={validationSchema}
            initialValues={initialFormValues}
            onSubmit={handleSubmit}
        >
            {({ values, handleSubmit, setValues, errors }) => (
                <>
                    <Modal.Header description={description}>{title}</Modal.Header>

                    <Modal.Body>
                        <h3 className="mb-6">{subtitle}</h3>
                        <BaseScheduleEditor
                            inModal
                            values={values}
                            errors={errors as any}
                            disablePastDays={disablePastDays}
                            weekdaysType={weekdaysType}
                            onChange={setValues}
                        />
                    </Modal.Body>

                    <Modal.Footer className="space-x-4">
                        <SecondaryButton
                            data-testid="base-schedule-editor-modal-cancel-button"
                            onClick={handleCancelClick}
                        >
                            Cancel
                        </SecondaryButton>

                        <Button
                            data-testid="base-schedule-editor-modal-apply-button"
                            type="button"
                            color="primary"
                            onClick={handleSubmit}
                        >
                            Apply
                        </Button>
                    </Modal.Footer>

                    <ScrollToFieldError />
                </>
            )}
        </XolaFormik>
    );
};
